import { TokenInfo, TokenInfoFromApi } from '@/types/token';
import { showProperDateString } from '@/utils/timeUtils';
import { getPairId } from '@/utils/transform/transformId';
import { CHAIN_ID, Context } from '@derivation-tech/context';
import { InstrumentCondition, MarketType } from '@synfutures/sdks-perp';

import { HISTORY_RANGE } from './constants';

export interface InstrumentHistoryBasicInfo {
  id: string;
  instrumentAddress: string;
  expiry: number;
  timestamp: number;
  txHash: string;
  symbol: string;
  baseToken: TokenInfoFromApi;
  quoteToken: TokenInfoFromApi;
}

export enum VirtualTradeType {
  MARKET = 'Market',
  LIMIT = 'Limit',
  RANGE = 'Liquidity',
  LIQUIDATION = 'Liquidation',
  TAKE_OVER = 'Take Over',
}

export interface TradeHistory extends InstrumentHistoryBasicInfo {
  size: string;
  balance: string;
  price: string;
  tradeFee: string;
  protocolFee: string;
  // type: number; // TOOD: 0/1
  typeString: VirtualTradeType;
  side: 'long' | 'short';
  // missing for now
  isRangeLiquidated?: boolean;
}

export interface OrderHistory extends Omit<InstrumentHistoryBasicInfo, 'txHash' | 'timestamp'> {
  placeTimestamp: number;
  placeTxHash: string;
  size: string;
  balance: string;
  takenSize: string;
  takenBalance: string;
  orderPrice: string;
  feeRebate: string;
  // type: number; // TOOD: 0/1
  cancelTimestamp: number;
  tradeValue: string;

  typeString: 'filled' | 'cancelled';
  side: 'long' | 'short';

  //  missing fields
  cancelTxHash: string;
  fillTimestamp: number;
  fillTxHash: string;
}

export interface LiquidityHistory extends InstrumentHistoryBasicInfo {
  amount: string;

  fairPrice: string;
  feeEarned: string;

  lowerPrice: string;
  lowerTick: number;
  // type: number;
  typeString: string; // TODO: 0/1
  upperPrice: string;
  upperTick: number;
}

export interface FundingHistory extends InstrumentHistoryBasicInfo {
  // type: number;
  typeString: 'receive' | 'pay'; // receive/pay
  value: string;
  valueUsd: string;
}
export interface TransferHistory extends InstrumentHistoryBasicInfo {
  typeString: 'scatter' | 'gather';
  value: string;
  valueUsd: string;
}

export interface AccountBalanceHistory {
  id: string;
  timestamp: number;
  tokenAddress: string;
  tokenInfo: TokenInfoFromApi;
  txHash: string;
  // type: number;
  typeString: 'deposit' | 'withdraw'; // deposit/withdraw
  value: string;
  valueUsd: string;
}

export interface IHistoryQuery {
  chainId: number;
  userAddress: string;
  timeRange: HISTORY_RANGE;
  instrumentAddress?: string;
  expiry?: number;
  page?: number;
  size?: number;
}

export interface IGraphInstrumentBasicInfo {
  chainId: number;
  id: string;
  instrumentAddress: string;
  expiries?: number[];
  symbol: string;
  isInverse: boolean;
  condition: InstrumentCondition;
  baseToken: TokenInfo;
  quoteToken: TokenInfo;
  marketType: MarketType;
}

export interface IGraphPairBasicInfo {
  chainId: number;
  id: string;
  expiry: number;
  symbol: string;
  isInverse: boolean;
  rootInstrument: IGraphInstrumentBasicInfo;
}

export function getEmptyPairFromBasicInstrument(
  instrument: IGraphInstrumentBasicInfo,
  expiry: number,
): IGraphPairBasicInfo {
  const pairId = getPairId(instrument.instrumentAddress, expiry);
  const chainId = instrument.chainId;

  return {
    id: pairId,
    expiry,
    isInverse: instrument.isInverse,
    rootInstrument: instrument,
    chainId,
    symbol: `${instrument.symbol}-${showProperDateString({
      expiry,
      format: 'MMDD',
      showShortPerp: true,
    })}`,
  };
}

/**
 * API Parameter Interfaces
 * These interfaces define the input parameters for graph API functions
 */

/**
 * Parameters for getAllPairBasicInfoByGraph function
 * @interface GetAllPairBasicInfoByGraphParams
 */
export interface GetAllPairBasicInfoByGraphParams {
  /** Chain ID for the blockchain network */
  chainId: number | undefined;
  /** SDK context instance for data source operations */
  sdk: Context | undefined;
}

/**
 * Response type for getAllPairBasicInfoByGraph function
 * @interface GetAllPairBasicInfoByGraphResponse
 */
export interface GetAllPairBasicInfoByGraphResponse {
  [pairId: string]: IGraphPairBasicInfo;
}

export interface HistoryRequestPairInfo {
  /** Trading instrument contract address */
  instrumentAddress?: string | undefined;
  /** Instrument expiry timestamp */
  expiry?: number | undefined;
}
export interface HistoryRequestBase {
  /** Chain ID for the blockchain network */
  chainId: CHAIN_ID | undefined;
  /** User wallet address */
  userAddress: string | undefined;
  /** Page number for pagination (optional) */
  page?: number;
  /** Number of items per page (optional, defaults to DEFAULT_HISTORY_PAGE_SIZE) */
  size?: number;
  /** Time range filter for historical data */
  timeRange: HISTORY_RANGE;
  /** Whether to fetch all data without pagination (optional, defaults to false) */
  download?: boolean;
}

/**
 * Parameters for getVirtualTradeHistory function
 * @interface GetVirtualTradeHistoryParams
 */
export interface GetTradeHistoryParams extends HistoryRequestBase, HistoryRequestPairInfo {}

/**
 * Response type for getVirtualTradeHistory function
 * @interface GetVirtualTradeHistoryResponse
 */
export type GetTradeHistoryResponse = TradeHistory[];

/**
 * Parameters for getOrdersHistory function
 * @interface GetOrdersHistoryParams
 */
export interface GetOrdersHistoryParams extends HistoryRequestBase, HistoryRequestPairInfo {}

/**
 * Response type for getOrdersHistory function
 * @interface GetOrdersHistoryResponse
 */
export type GetOrdersHistoryResponse = (OrderHistory & { logIndex?: number })[];

/**
 * Parameters for getFundingHistory function
 * @interface GetFundingHistoryParams
 */
export interface GetFundingHistoryParams extends HistoryRequestBase, HistoryRequestPairInfo {}

/**
 * Response type for getFundingHistory function
 * @interface GetFundingHistoryResponse
 */
export type GetFundingHistoryResponse = FundingHistory[];

/**
 * Parameters for getTransferHistory function
 * @interface GetTransferHistoryParams
 */
export interface GetTransferHistoryParams extends HistoryRequestBase, HistoryRequestPairInfo {}

/**
 * Response type for getTransferHistory function
 * @interface GetTransferHistoryResponse
 */
export type GetTransferHistoryResponse = (TransferHistory & {
  timestamp: number;
  instrumentAddress: string;
  expiry: number;
  isTransferIn: boolean;
  txHash: string;
  logIndex: number;
})[];

/**
 * Parameters for getLiquidityHistory function
 * @interface GetLiquidityHistoryParams
 */
export interface GetLiquidityHistoryParams extends HistoryRequestBase, HistoryRequestPairInfo {}

/**
 * Response type for getLiquidityHistory function
 * @interface GetLiquidityHistoryResponse
 */
export type GetLiquidityHistoryResponse = LiquidityHistory[];

/**
 * Parameters for getAccountBalanceHistory function
 * @interface GetAccountBalanceHistoryParams
 */
export interface GetAccountBalanceHistoryParams extends HistoryRequestBase {}

/**
 * Response type for getAccountBalanceHistory function
 * @interface GetAccountBalanceHistoryResponse
 */
export type GetAccountBalanceHistoryResponse = AccountBalanceHistory[];
